import { FC, useEffect, useMemo, useState } from 'react';
import { PreloadedQuery, usePreloadedQuery } from 'react-relay';
import ExitSlipModalBodyData from './ExitSlipModalBodyData';
import { ExitSlipRowVariant } from './ExitSlipLessonRow';
import { ExitSlipCurriculumLessonsQuery } from '../../graphQLOperations/__generated__/ExitSlipCurriculumLessonsQuery.graphql';
import { getClassExitSlipDueOnByClasses, getCurriculumLessonsByFilter } from '../../graphQLOperations/ExitSlip';
import { useAppSelector } from '../../redux/hooks';
import { selectedExitSlipFilter } from '../../redux/reducers/filterExitSlipSlice';
import { LessonDate } from './ExitSlipModalContainer';
import { splitId } from '../../businessLogicService/splitIdsFromBackEnd';
import { sortByLabelWithDot } from '../../businessLogicService/sortCriteria';
import { ExitSlipClassExitSlipDueOnByClassesQuery } from '../../graphQLOperations/__generated__/ExitSlipClassExitSlipDueOnByClassesQuery.graphql';
import { getDayClaims, getStagedDayClaims, unitLabel } from '../../businessLogicService/exitSlipSnapshotHelper';

interface Props {
  queryReference: PreloadedQuery<ExitSlipCurriculumLessonsQuery, Record<string, unknown>>;
  queryDisabledDatesReference: PreloadedQuery<ExitSlipClassExitSlipDueOnByClassesQuery, Record<string, unknown>>;
  setNoDataYet: (d: boolean) => void;
  lessonDates: LessonDate[];
  setLessonDates: (a: LessonDate[]) => void;
  setSaveButtonDisabled: (s: boolean) => void;
}
export interface LessonItem {
  id: string;
  title: string;
  unit: string;
  targetId: string;
  date?: Date | null;
  classExitSlip?: string | null;
  activityId: string | undefined;
  //the day a canceled ticket was due, which is what the row reports and what its day releases
  canceledDate?: Date | null;
}

const ExitSlipModalAssignBodyQuery: FC<Props> = ({
  queryReference,
  queryDisabledDatesReference,
  setNoDataYet,
  lessonDates,
  setLessonDates,
  setSaveButtonDisabled,
}) => {
  const exitSlipFilterRedux = useAppSelector(selectedExitSlipFilter);
  const [lessonToShow, setLessonToShow] = useState<LessonItem[]>([]);

  const disabledDatesData = usePreloadedQuery<ExitSlipClassExitSlipDueOnByClassesQuery>(
    getClassExitSlipDueOnByClasses,
    queryDisabledDatesReference,
  );
  const data = usePreloadedQuery<ExitSlipCurriculumLessonsQuery>(getCurriculumLessonsByFilter, queryReference);

  useEffect(() => {
    //TEAMB-3483 a canceled ticket does not hold its lesson: the day was called off, so the lesson
    //comes back to this list. TEAMB-3569 re-assigning it schedules a new row and leaves the
    //canceled one, with its responses, on the day that was called off.
    const isSelected = (classId: string | undefined | null) =>
      !!classId && exitSlipFilterRedux.classes.includes(classId);

    const unassigned = data.curriculumLessons.filter(
      lesson => !lesson.classExitSlips?.some(el => !el?.canceledOn && isSelected(el?.class?.id)),
    );

    //the query is roadmap-wide now that Unit is optional, so scope on the client when it is set
    const scoped = exitSlipFilterRedux.unit
      ? unassigned.filter(lesson => lesson.target?.id === exitSlipFilterRedux.unit)
      : unassigned;

    if (exitSlipFilterRedux.classes.length) {
      setLessonToShow(
        [...scoped]
          .sort((a, b) => sortByLabelWithDot({ label: String(a.number) }, { label: String(b.number) }))
          .map(el => ({
            title: `Lesson ${el.number}: ${el.name}`,
            id: el.id,
            unit: unitLabel(el.target?.name),
            targetId: el.target?.id ?? '',
            activityId: el?.curriculumLessonTargetSkills?.[0]?.targetSkill?.skill?.code
              ? `ExitSlip_Miami_${splitId(el?.id as string)}_${el?.curriculumLessonTargetSkills[0].targetSkill.skill
                  .code}`
              : undefined,
          })),
      );
      setNoDataYet(false);
    } else {
      setNoDataYet(true);
    }
  }, [data, exitSlipFilterRedux.classes, exitSlipFilterRedux.unit]);

  //nothing in this list is editable here — every row on screen is unassigned
  const { savedClaims, pastTags } = useMemo(
    () => getDayClaims(disabledDatesData.classExitSlips, []),
    [disabledDatesData],
  );

  const stagedClaims = useMemo(() => getStagedDayClaims(lessonToShow, lessonDates), [lessonToShow, lessonDates]);

  return (
    <div className="tw-flex tw-flex-col tw-gap-6 tw-p-8">
      <h3 className="tw-m-0 tw-font-sora tw-text-xl tw-font-bold tw-leading-7 tw-text-neutral-950">
        Unassigned lessons
      </h3>
      <ExitSlipModalBodyData
        variant={ExitSlipRowVariant.UNASSIGNED}
        lessons={lessonToShow}
        lessonDates={lessonDates}
        setLessonDates={setLessonDates}
        setSaveButtonDisabled={setSaveButtonDisabled}
        savedClaims={savedClaims}
        stagedClaims={stagedClaims}
        pastTags={pastTags}
        emptyState={
          <div className="nc_exit-slip-lesson-done">
            {/*TEAMB-2608: nothing authored is not the same as everything assigned*/}
            <h4>
              {data.curriculumLessons.length
                ? "You've assigned every exit ticket for this selection 🎉"
                : 'No exit tickets have been created for this selection.'}
            </h4>
          </div>
        }
      />
    </div>
  );
};

export default ExitSlipModalAssignBodyQuery;
